/**
 * LayoutContext provides a way for child components to dynamically configure
 * the global AppLayout (navigation, breadcrumbs, tools, etc.)
 *
 * Data-driven approach:
 * - Stores serializable data instead of React elements where possible
 * - GlobalAppLayout renders components based on configuration data
 * - Prevents infinite loops from React element reference changes
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  DEFAULT_LAYOUT_CONFIG,
  LayoutConfig,
  LayoutConfigUpdate,
  LayoutContextValue,
} from '../types/layout/layout.types';

/**
 * Context for layout configuration
 */
const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

/**
 * Props for LayoutProvider component
 */
interface LayoutProviderProps {
  children: React.ReactNode;
}

/**
 * Deep equality check for breadcrumb arrays
 */
const areBreadcrumbsEqual = (
  a: Array<{ text: string; href?: string }>,
  b: Array<{ text: string; href?: string }>
): boolean => {
  if (a.length !== b.length) return false;
  return a.every((item, index) => {
    const otherItem = b[index];
    return item.text === otherItem.text && item.href === otherItem.href;
  });
};

/**
 * LayoutProvider component
 *
 * Wraps the application and provides layout configuration context
 * to all child components.
 *
 * @example
 * ```tsx
 * <LayoutProvider>
 *   <App />
 * </LayoutProvider>
 * ```
 */
export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const [config, setConfigState] = useState<LayoutConfig>(DEFAULT_LAYOUT_CONFIG);
  const [navigationOpen, setNavigationOpenState] = useState(true);

  /**
   * Update layout configuration
   * Merges new config with existing config
   * Only triggers re-render if values actually changed
   */
  const setConfig = useCallback((newConfig: LayoutConfigUpdate) => {
    setConfigState((prevConfig) => {
      // Check if any values actually changed
      let hasChanges = false;

      // Check primitive values
      if (newConfig.navigationType !== undefined && newConfig.navigationType !== prevConfig.navigationType) {
        hasChanges = true;
      }
      if (newConfig.navigationHide !== undefined && newConfig.navigationHide !== prevConfig.navigationHide) {
        hasChanges = true;
      }
      if (newConfig.contentType !== undefined && newConfig.contentType !== prevConfig.contentType) {
        hasChanges = true;
      }
      if (newConfig.maxContentWidth !== undefined && newConfig.maxContentWidth !== prevConfig.maxContentWidth) {
        hasChanges = true;
      }

      // Check breadcrumbs array (compare by content, not reference)
      if (newConfig.breadcrumbs !== undefined) {
        if (!areBreadcrumbsEqual(newConfig.breadcrumbs, prevConfig.breadcrumbs)) {
          hasChanges = true;
        }
      }

      // Check pageFlashbarItems (compare by length for now - content changes handled by component)
      if (newConfig.pageFlashbarItems !== undefined) {
        if (newConfig.pageFlashbarItems.length !== prevConfig.pageFlashbarItems.length) {
          hasChanges = true;
        }
      }

      // Check stickyHeader config
      if (newConfig.stickyHeader !== undefined) {
        if (
          newConfig.stickyHeader.enabled !== prevConfig.stickyHeader.enabled ||
          newConfig.stickyHeader.component !== prevConfig.stickyHeader.component
        ) {
          hasChanges = true;
        }
      }

      // Check tools config
      if (newConfig.tools !== undefined) {
        if (
          newConfig.tools.hide !== prevConfig.tools.hide ||
          newConfig.tools.open !== prevConfig.tools.open ||
          newConfig.tools.width !== prevConfig.tools.width ||
          newConfig.tools.component !== prevConfig.tools.component
        ) {
          hasChanges = true;
        }
      }

      // Check splitPanel config
      if (newConfig.splitPanel !== undefined) {
        if (
          newConfig.splitPanel.open !== prevConfig.splitPanel.open ||
          newConfig.splitPanel.size !== prevConfig.splitPanel.size ||
          newConfig.splitPanel.component !== prevConfig.splitPanel.component
        ) {
          hasChanges = true;
        }
      }

      // If nothing changed, return previous config to prevent re-render
      if (!hasChanges) {
        return prevConfig;
      }

      // Merge configs
      return {
        ...prevConfig,
        ...newConfig,
        // Deep merge nested objects
        stickyHeader: newConfig.stickyHeader
          ? { ...prevConfig.stickyHeader, ...newConfig.stickyHeader }
          : prevConfig.stickyHeader,
        tools: newConfig.tools ? { ...prevConfig.tools, ...newConfig.tools } : prevConfig.tools,
        splitPanel: newConfig.splitPanel
          ? { ...prevConfig.splitPanel, ...newConfig.splitPanel }
          : prevConfig.splitPanel,
      };
    });
  }, []);

  /**
   * Reset layout configuration to defaults
   */
  const resetConfig = useCallback(() => {
    setConfigState(DEFAULT_LAYOUT_CONFIG);
  }, []);

  /**
   * Update navigationOpen state
   * Called from GlobalAppLayout when drawer is toggled
   */
  const setNavigationOpen = useCallback((open: boolean) => {
    setNavigationOpenState(open);
  }, []);

  const value = useMemo(
    () => ({
      config,
      navigationOpen,
      setConfig,
      resetConfig,
      setNavigationOpen,
    }),
    [config, navigationOpen, setConfig, resetConfig, setNavigationOpen]
  );

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

/**
 * Hook to access layout configuration context
 *
 * @throws Error if used outside of LayoutProvider
 *
 * @example
 * ```tsx
 * const MyPage = () => {
 *   const { setConfig } = useLayoutConfig();
 *
 *   useEffect(() => {
 *     setConfig({
 *       navigationType: 'manage-programs',
 *       breadcrumbs: [{ text: 'Home', href: '/' }, { text: 'My Page' }],
 *     });
 *   }, [setConfig]);
 *
 *   return <div>Page content</div>;
 * };
 * ```
 */
export const useLayoutConfig = (): LayoutContextValue => {
  const context = useContext(LayoutContext);

  if (context === undefined) {
    throw new Error('useLayoutConfig must be used within a LayoutProvider');
  }

  return context;
};
